import { html, render } from 'lit-html';

import { assert } from '@disy/lib/custom-error';

export class ChangePageSizeEvent extends CustomEvent<{
  /* Number of rows per page */
  pageSize: number;
}> {
  static get type() {
    return 'changePageSize' as const;
  }

  constructor(pageSize: number) {
    super(ChangePageSizeEvent.type, { bubbles: true, detail: { pageSize } });
  }
}

declare global {
  interface HTMLElementEventMap {
    [ChangePageSizeEvent.type]: ChangePageSizeEvent;
  }
}

const COMPONENT_NAME = 'd-pagination-control-page-size-picker';
const DEFAULT_OPTIONS: number[] = [10, 25, 50, 100, 250];

class PaginationControlPageSizePicker extends HTMLElement {
  static get observedAttributes() {
    return ['options', 'value'];
  }

  attributeChangedCallback() {
    this.#render();
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    this.#render();
  }

  #render() {
    const value = this.value;
    render(
      html`
        <select class="form-control" @change=${(event: Event) => this.#onChange(event)}>
          ${this.options.map((option) => html`<option ?selected=${option === value}>${option}</option>`)}
        </select>
      `,
      this,
    );
  }

  #onChange(event: Event) {
    event.stopImmediatePropagation();
    this.value = Number((event.target as HTMLSelectElement).value);
    this.dispatchEvent(new ChangePageSizeEvent(this.value));
  }

  get options() {
    let options = DEFAULT_OPTIONS;

    const rawAttributeOptions = this.getAttribute('options');
    if (rawAttributeOptions !== null) {
      assert(/^(\d+)(,\d+)*$/.test(rawAttributeOptions), 'options not in the format "number,number,number"');
      const rawAttributeOptionStrings = rawAttributeOptions.split(',');
      assert(
        rawAttributeOptionStrings.length === new Set(rawAttributeOptionStrings).size,
        'options must not contain duplicates',
      );
      options = rawAttributeOptionStrings.map((option) => parseInt(option));
    }

    return options;
  }

  get value() {
    const valueString = this.getAttribute('value');
    if (valueString) {
      const value = Number.parseInt(valueString);
      this.#assertValidValue(value);
      return value;
    }
    return this.options[0];
  }

  set value(value: number) {
    this.#assertValidValue(value);
    this.setAttribute('value', String(value));
  }

  #assertValidValue(value: number) {
    assert(this.options.includes(value), `invalid value ${value} (valid values are [${this.options}])`);
  }
}

customElements.define(COMPONENT_NAME, PaginationControlPageSizePicker);
declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_NAME]: PaginationControlPageSizePicker;
  }
}
